import React, { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { doc, onSnapshot, collection, query, where, getDocs, getDoc, addDoc, setDoc } from "firebase/firestore";
import { db as firestoreDb } from "@/src/lib/firebase";
import { useAuth } from "@/src/lib/AuthContext";
import { Shop, Product, Review } from "@/src/types";
import { ProductCard } from "../components/ProductCard";
import { Loader2, MapPin, Phone, MessageCircle, ArrowLeft, Star, Search, Filter, ArrowUpDown, Globe, Mail, Share2, Instagram, Facebook, Calendar, Clock, Video, Zap, LayoutDashboard, User, MessageSquare, Award, Store } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogFooter, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from "@/components/ui/dialog";
import { getWhatsAppLink, shortenUrl } from "@/src/lib/utils";
import { ShareModal } from "../components/ShareModal";
import { toast } from "sonner";
import { MALL_ZONES } from "@/src/constants";

export function ShopDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [shop, setShop] = useState<Shop | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isMeetingDialogOpen, setIsMeetingDialogOpen] = useState(false);
  const [isSubmittingMeeting, setIsSubmittingMeeting] = useState(false);
  const [meetingForm, setMeetingForm] = useState({
    title: "",
    date: "",
    time: "",
    description: ""
  });

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [priceOrder, setPriceOrder] = useState<"asc" | "desc" | "none">("none");
  const [stockFilter, setStockFilter] = useState<"all" | "available" | "out">("all");

  useEffect(() => {
    if (!id) return;

    const shopRef = doc(firestoreDb, "shops", id);
    const unsubscribe = onSnapshot(shopRef, async (shopSnap) => {
      if (shopSnap.exists()) {
        const shopData = { ...shopSnap.data(), id: shopSnap.id } as Shop;
        // List of test emails with premium privileges
        const testEmails = ['kensudev@gmail.com', 'kensudev@outlook.com', 'kensudev@yahoo.com'];
        if (shopData.email && (testEmails.includes(shopData.email) || shopData.email.startsWith('kensudev'))) {
          shopData.plan = 'premium';
        }
        setShop(shopData);
        
        // Fetch products for this shop
        const productsQuery = query(collection(firestoreDb, "products"), where("shopId", "==", id));
        const productsSnap = await getDocs(productsQuery);
        const productsData = productsSnap.docs.map(d => ({ ...d.data(), id: d.id } as Product));
        setProducts(productsData);

        // Fetch reviews for this shop and its products (visible only)
        const shopReviewsQuery = query(
          collection(firestoreDb, "reviews"), 
          where("targetId", "==", id)
        );
        const shopReviewsSnap = await getDocs(shopReviewsQuery);
        let allReviews = shopReviewsSnap.docs.map(d => ({ ...d.data(), id: d.id } as Review)).filter(r => r.isVisible);

        if (productsData.length > 0) {
          const productIds = productsData.map(p => p.id);
          for (let i = 0; i < productIds.length; i += 10) {
            const chunk = productIds.slice(i, i + 10);
            const pReviewsQuery = query(
              collection(firestoreDb, "reviews"),
              where("targetId", "in", chunk)
            );
            const pReviewsSnap = await getDocs(pReviewsQuery);
            const pReviews = pReviewsSnap.docs.map(d => ({ ...d.data(), id: d.id } as Review)).filter(r => r.isVisible);
            allReviews = [...allReviews, ...pReviews];
          }
        }
        
        setReviews(allReviews.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
        setLoading(false);
      } else {
        setShop(null);
        setLoading(false);
      }
    }, (error) => {
      console.error("Error listening to shop:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [id]);

  const filteredAndSortedProducts = useMemo(() => {
    let result = [...products];

    // Search filter
    if (searchTerm) {
      result = result.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Stock filter
    if (stockFilter === "available") {
      result = result.filter(p => (p.stock ?? 0) > 0);
    } else if (stockFilter === "out") {
      result = result.filter(p => (p.stock ?? 0) === 0);
    }

    // Price sort
    if (priceOrder === "asc") {
      result.sort((a, b) => a.price - b.price);
    } else if (priceOrder === "desc") {
      result.sort((a, b) => b.price - a.price);
    }

    return result;
  }, [products, searchTerm, priceOrder, stockFilter]);

  const handleShare = () => {
    setIsShareModalOpen(true);
  };

  const handleBookMeeting = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !shop) {
      toast.error("Debes iniciar sesión para agendar una cita");
      return;
    }

    setIsSubmittingMeeting(true);
    try {
      if (!meetingForm.date || !meetingForm.time) {
        toast.error("Por favor selecciona fecha y hora");
        setIsSubmittingMeeting(false);
        return;
      }

      // Business Hours Validation
      const selectedDate = new Date(`${meetingForm.date}T00:00:00`);
      const day = selectedDate.getDay();
      const shopHours = shop.businessHours?.find(bh => bh.day === day);
      const DAYS_ES = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

      if (shopHours) {
        if (!shopHours.active) {
          toast.error(`La tienda no atiende los días ${DAYS_ES[day]}`);
          setIsSubmittingMeeting(false);
          return;
        }
        if (meetingForm.time < shopHours.open || meetingForm.time > shopHours.close) {
          toast.error(`Horario fuera de atención para el ${DAYS_ES[day]}: de ${shopHours.open} a ${shopHours.close}`);
          setIsSubmittingMeeting(false);
          return;
        }
      }

      const start = new Date(`${meetingForm.date}T${meetingForm.time}`);
      if (isNaN(start.getTime())) {
        toast.error("Fecha u hora inválida");
        setIsSubmittingMeeting(false);
        return;
      }

      const startTime = start.toISOString();
      const endTime = new Date(start.getTime() + 60 * 60 * 1000).toISOString(); // 1 hour duration

      // Create meeting in Firestore (CLIENT SIDE - more reliable)
      const meetingData = {
        shopId: shop.id,
        buyerUid: user.uid,
        buyerName: user.displayName || "Cliente",
        buyerEmail: user.email || "",
        title: meetingForm.title || `Cita con ${user.displayName || "Cliente"}`,
        description: meetingForm.description || "",
        startTime,
        endTime,
        status: "pending" as const,
        meetLink: "", // Will be generated on approval
        createdAt: new Date().toISOString()
      };

      const docRef = await addDoc(collection(firestoreDb, "meetings"), meetingData);
      
      // Update meetLink with the ID (internally /meeting/ID)
      // This is safe to do immediately or we can leave it for the approval step
      // The user wants it internally.
      
      // Also ensure client is in CRM (CLIENT SIDE)
      try {
        const clientId = `${shop.id}_${user.uid}`;
        const clientRef = doc(firestoreDb, "crm_clients", clientId);
        const clientSnap = await getDoc(clientRef);
        
        const clientData = {
          shopId: shop.id,
          uid: user.uid,
          name: user.displayName || "Cliente",
          email: user.email || "",
          stage: "lead",
          lastInteraction: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        if (!clientSnap.exists()) {
          await setDoc(clientRef, {
            ...clientData,
            id: clientId,
            createdAt: new Date().toISOString()
          });
        }
      } catch (crmError) {
        console.error("Non-blocking CRM error:", crmError);
      }

      toast.success("¡Solicitud de cita enviada! La tienda debe confirmarla.");
      setIsMeetingDialogOpen(false);
      setMeetingForm({ title: "", date: "", time: "", description: "" });
    } catch (error) {
      console.error("Error booking meeting:", error);
      toast.error("Error al agendar la cita. Por favor intenta de nuevo.");
    } finally {
      setIsSubmittingMeeting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="container py-24 text-center">
        <h2 className="text-2xl font-bold mb-4">Tienda no encontrada</h2>
        <Link to="/shops">
          <Button>Volver a explorar</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-8 pb-16">
      {/* Banner */}
      <div className="relative h-[300px] md:h-[400px] w-full overflow-hidden">
        <img 
          src={shop.bannerURL || `https://picsum.photos/seed/${shop.id}/1920/600`} 
          alt={shop.name}
          className="w-full h-full object-cover"
          referrerPolicy="no-referrer"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20" />
        <div className="absolute bottom-0 left-0 w-full p-8 text-white">
          <div className="container px-4 md:px-8 flex flex-col md:flex-row items-end gap-6">
            <div className="h-24 w-24 md:h-32 md:w-32 rounded-full border-4 border-background overflow-hidden bg-white shadow-xl">
              <img 
                src={shop.logoURL || `https://picsum.photos/seed/${shop.name}/200/200`} 
                alt={shop.name}
                className="h-full w-full object-cover"
                referrerPolicy="no-referrer"
              />
            </div>
            <div className="flex-1 mb-2">
              <div className="flex items-center flex-wrap gap-2">
                <h1 className="text-3xl md:text-5xl font-bold">{shop.name}</h1>
                <div className="flex gap-2">
                  {shop.plan === 'premium' && (
                    <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white border-none gap-1 py-1">
                      <Zap className="h-3 w-3 fill-white" />
                      Premium
                    </Badge>
                  )}
                  {/* Certification Badge simulated for demo - shops with kensudev in email are certified */}
                  {shop.email?.includes('kensudev') && (
                    <Badge className="bg-emerald-500 text-white border-none gap-1 py-1 shadow-lg shadow-emerald-500/20">
                      <Award className="h-3 w-3 fill-white" />
                      Vendedor Certificado
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4 mt-2 text-sm md:text-base opacity-90">
                <div className="flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  {shop.location.city}, {shop.location.country}
                </div>
                <div className="flex items-center">
                  <Star className="h-4 w-4 text-yellow-400 fill-yellow-400 mr-1" />
                  {(shop.rating || 0).toFixed(1)} ({shop.reviewCount || 0} reseñas)
                </div>
                {shop.mallZone && shop.mallZone !== 'otros' && (
                  <div className="flex items-center bg-white/20 px-2 py-0.5 rounded text-xs">
                    <Store className="h-3 w-3 mr-1" />
                    {MALL_ZONES.find(z => z.id === shop.mallZone)?.label}
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mb-2">
              <Button 
                variant="secondary" 
                size="icon" 
                className="rounded-full"
                onClick={handleShare}
                title="Compartir tienda"
              >
                <Share2 className="h-4 w-4" />
              </Button>
              
              {shop.instagram && (
                <Button 
                  variant="secondary" 
                  size="icon" 
                  className="rounded-full"
                  onClick={() => window.open(`https://instagram.com/${shop.instagram!.replace('@', '')}`, "_blank")}
                  title="Instagram"
                >
                  <Instagram className="h-4 w-4" />
                </Button>
              )}
              
              {shop.facebook && (
                <Button 
                  variant="secondary" 
                  size="icon" 
                  className="rounded-full"
                  onClick={() => window.open(shop.facebook!.startsWith('http') ? shop.facebook! : `https://facebook.com/${shop.facebook!}`, "_blank")}
                  title="Facebook"
                >
                  <Facebook className="h-4 w-4" />
                </Button>
              )}

              {user?.uid === shop.ownerUid && (
                <Link to="/dashboard">
                  <Button variant="secondary" className="gap-2">
                    Administrar Tienda
                  </Button>
                </Link>
              )}
              
              {user ? (
                <>
                  <Button 
                    variant="outline"
                    className="gap-2 border-primary text-primary hover:bg-primary/5"
                    onClick={async () => {
                      if (!user) {
                        toast.error("Inicia sesión para chatear");
                        return;
                      }
                      try {
                        // Create or find chat
                        const chatsQuery = query(
                          collection(firestoreDb, "chats"), 
                          where("participants", "array-contains", user.uid)
                        );
                        const chatsSnap = await getDocs(chatsQuery);
                        let chat = chatsSnap.docs.find(d => d.data().participants.includes(shop.ownerUid));
                        
                        if (!chat) {
                          const newChatRef = await addDoc(collection(firestoreDb, "chats"), {
                            participants: [user.uid, shop.ownerUid],
                            participantNames: {
                              [user.uid]: user.displayName || "Cliente",
                              [shop.ownerUid]: shop.name
                            },
                            shopId: shop.id,
                            updatedAt: new Date().toISOString(),
                            lastMessage: ""
                          });
                          toast.success("Chat iniciado");
                        }
                        navigate("/dashboard"); // Redirect to dashboard where chat is
                      } catch (error) {
                        toast.error("Error al iniciar chat");
                      }
                    }}
                  >
                    <MessageSquare className="h-4 w-4" /> Contactar
                  </Button>

                  {shop.whatsapp && (
                    <Button 
                      className="gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white border-none"
                      onClick={() => window.open(getWhatsAppLink(shop.whatsapp!, `Hola ${shop.name}! Vengo de EmpreMarket y tengo una consulta.`), "_blank")}
                    >
                      <MessageCircle className="h-4 w-4" /> WhatsApp
                    </Button>
                  )}

                  {/* Appointment Scheduling Button - Available for all shops for now */}
                  <Dialog open={isMeetingDialogOpen} onOpenChange={setIsMeetingDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="gap-2 bg-blue-600 hover:bg-blue-700 text-white border-none">
                        <Calendar className="h-4 w-4" /> Agendar Cita
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[425px]">
                      <DialogHeader>
                        <DialogTitle>Solicitar Cita con {shop.name}</DialogTitle>
                        <DialogDescription>
                          Envía una solicitud de reunión virtual. Una vez aceptada, podrás entrar a la sala de video directamente desde EmpreMarket.
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleBookMeeting} className="space-y-4 py-4">
                          <div className="grid gap-2">
                            <Label htmlFor="meeting-title">Asunto de la reunión</Label>
                            <Input 
                              id="meeting-title" 
                              placeholder="Ej: Consulta sobre pedido personalizado" 
                              value={meetingForm.title}
                              onChange={(e) => setMeetingForm({...meetingForm, title: e.target.value})}
                              required 
                            />
                          </div>
                          
                          {shop.businessHours && shop.businessHours.some(h => h.active) && (
                            <div className="p-3 bg-muted/30 rounded-lg text-[10px] space-y-1">
                              <p className="font-bold flex items-center gap-1"><Clock className="h-3 w-3" /> Horarios de atención para citas:</p>
                              <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                                {shop.businessHours.filter(h => h.active).map(h => (
                                   <div key={h.day} className="flex justify-between">
                                     <span>{["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"][h.day]}:</span>
                                     <span>{h.open} - {h.close}</span>
                                   </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <div className="grid grid-cols-2 gap-4">
                            <div className="grid gap-2">
                              <Label htmlFor="meeting-date">Fecha</Label>
                              <Input 
                                id="meeting-date" 
                                type="date" 
                                value={meetingForm.date}
                                onChange={(e) => setMeetingForm({...meetingForm, date: e.target.value})}
                                required 
                              />
                            </div>
                            <div className="grid gap-2">
                              <Label htmlFor="meeting-time">Hora</Label>
                              <Input 
                                id="meeting-time" 
                                type="time" 
                                value={meetingForm.time}
                                onChange={(e) => setMeetingForm({...meetingForm, time: e.target.value})}
                                required 
                              />
                            </div>
                          </div>
                          <div className="grid gap-2">
                            <Label htmlFor="meeting-desc">Descripción adicional</Label>
                            <Textarea 
                              id="meeting-desc" 
                              placeholder="Cuéntanos un poco más sobre lo que quieres tratar..." 
                              value={meetingForm.description}
                              onChange={(e) => setMeetingForm({...meetingForm, description: e.target.value})}
                            />
                          </div>
                          <DialogFooter>
                            <Button type="submit" disabled={isSubmittingMeeting} className="w-full font-bold">
                              {isSubmittingMeeting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                              Solicitar Cita
                            </Button>
                          </DialogFooter>
                        </form>
                      </DialogContent>
                    </Dialog>
                </>
              ) : (
                <Button 
                  className="gap-2" 
                  variant="default"
                  onClick={() => navigate('/auth')}
                >
                  <User className="h-4 w-4" /> Inicia sesión para contactar
                </Button>
              )}
              </div>
            </div>
          </div>
        </div>

      <div className="container px-4 md:px-8 grid gap-8 md:grid-cols-3">
        {/* Sidebar Info */}
        <div className="flex flex-col gap-6">
          <div className="bg-muted/30 p-6 rounded-2xl">
            <h3 className="font-bold text-lg mb-4">Sobre la tienda</h3>
            <p className="text-muted-foreground text-sm leading-relaxed">
              {shop.description}
            </p>
            <Separator className="my-6" />
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Categorías:</span>
                <div className="flex flex-wrap gap-1 justify-end">
                  {shop.categories.map(cat => (
                    <Badge key={cat} variant="secondary" className="text-[10px]">{cat}</Badge>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-3 mt-4">
                <div className="flex gap-2">
                  {shop.whatsapp && user && (
                    <Button asChild className="flex-1 gap-2" variant="outline">
                      <a href={`tel:${shop.whatsapp.replace(/\D/g, '')}`}>
                        <Phone className="h-4 w-4" /> Llamar
                      </a>
                    </Button>
                  )}
                  {shop.email && user && (
                    <Button asChild className="flex-1 gap-2" variant="outline">
                      <a href={`mailto:${shop.email}`}>
                        <Mail className="h-4 w-4" /> Email
                      </a>
                    </Button>
                  )}
                </div>
                {!user && (
                  <p className="text-[10px] text-center text-muted-foreground italic">
                    Inicia sesión para ver datos de contacto
                  </p>
                )}
              </div>
              {shop.website && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Globe className="h-3 w-3" /> Web:
                  </span>
                  <a 
                    href={shop.website.startsWith('http') ? shop.website : `https://${shop.website}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="font-medium text-primary hover:underline truncate max-w-[150px]"
                  >
                    {shop.website.replace(/^https?:\/\//, '')}
                  </a>
                </div>
              )}
              {shop.instagram && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Instagram className="h-3 w-3" /> Instagram:
                  </span>
                  <a 
                    href={`https://instagram.com/${shop.instagram.replace('@', '')}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="font-medium text-primary hover:underline"
                  >
                    {shop.instagram.startsWith('@') ? shop.instagram : `@${shop.instagram}`}
                  </a>
                </div>
              )}
              {shop.facebook && (
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground flex items-center gap-1">
                    <Facebook className="h-3 w-3" /> Facebook:
                  </span>
                  <a 
                    href={shop.facebook.startsWith('http') ? shop.facebook : `https://facebook.com/${shop.facebook}`} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="font-medium text-primary hover:underline"
                  >
                    {shop.facebook.replace(/^https?:\/\/(www\.)?facebook\.com\//, '')}
                  </a>
                </div>
              )}

              {shop.plan === 'premium' && shop.physicalLocation && (
                <div className="mt-6 space-y-4 pt-6 border-t">
                  <h3 className="font-bold text-lg flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-primary" />
                    Ubicación Física
                  </h3>
                  <div className="w-full h-[250px] rounded-2xl overflow-hidden border shadow-inner bg-muted/50">
                    <iframe
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      style={{ border: 0 }}
                      src={`https://www.google.com/maps/embed/v1/place?key=${import.meta.env.VITE_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY'}&q=${shop.physicalLocation.lat},${shop.physicalLocation.lng}`}
                      allowFullScreen
                      referrerPolicy="no-referrer-when-downgrade"
                    ></iframe>
                  </div>
                  <p className="text-xs text-muted-foreground italic">
                    {shop.physicalLocation.address}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {shop && (
          <ShareModal 
            isOpen={isShareModalOpen}
            onOpenChange={setIsShareModalOpen}
            originalUrl={window.location.href}
            defaultTitle={`${shop.name} | EmpreMarket`}
            defaultDescription={shop.description}
            defaultImage={shop.bannerURL || shop.logoURL || ""}
            shopName={shop.name}
            availableImages={shop.premiumPhotos}
          />
        )}

        {/* Products Grid */}
        <div className="md:col-span-2">
          {shop.plan === 'premium' && shop.premiumPhotos && shop.premiumPhotos.length > 0 && (
            <div className="mb-12">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                <LayoutDashboard className="h-6 w-6 text-primary" />
                Galería del Emprendimiento
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {shop.premiumPhotos.map((photo, idx) => (
                  <div key={idx} className="aspect-square rounded-2xl overflow-hidden border shadow-sm hover:shadow-md transition-shadow">
                    <img 
                      src={photo} 
                      alt={`Galería ${idx + 1}`} 
                      className="w-full h-full object-cover"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-col gap-6 mb-8">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold">Catálogo de Productos</h2>
              <Badge variant="outline">{filteredAndSortedProducts.length} items</Badge>
            </div>

            {/* Filters Bar */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input 
                  placeholder="Buscar en esta tienda..." 
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <div className="flex items-center gap-2">
                <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                <select 
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  value={priceOrder}
                  onChange={(e) => setPriceOrder(e.target.value as any)}
                >
                  <option value="none">Ordenar por precio</option>
                  <option value="asc">Precio: Menor a Mayor</option>
                  <option value="desc">Precio: Mayor a Menor</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <select 
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  value={stockFilter}
                  onChange={(e) => setStockFilter(e.target.value as any)}
                >
                  <option value="all">Todos los productos</option>
                  <option value="available">Solo disponibles</option>
                  <option value="out">Sin stock</option>
                </select>
              </div>
            </div>
          </div>

          {filteredAndSortedProducts.length === 0 ? (
            <div className="text-center py-24 border rounded-2xl border-dashed bg-muted/10">
              <p className="text-muted-foreground">No se encontraron productos con los filtros seleccionados.</p>
              {(searchTerm || priceOrder !== "none" || stockFilter !== "all") && (
                <Button 
                  variant="link" 
                  onClick={() => {
                    setSearchTerm("");
                    setPriceOrder("none");
                    setStockFilter("all");
                  }}
                >
                  Limpiar filtros
                </Button>
              )}
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2">
              {filteredAndSortedProducts.map(product => (
                <ProductCard 
                  key={product.id} 
                  product={product} 
                  shopWhatsApp={shop.whatsapp} 
                  shopName={shop.name}
                />
              ))}
            </div>
          )}

          <Separator className="my-12" />

          {/* Shop Reviews Section */}
          <div className="space-y-8">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              Reseñas de la Tienda
              <Badge variant="outline" className="ml-2">{reviews.length}</Badge>
            </h2>

            {reviews.length === 0 ? (
              <div className="bg-muted/30 rounded-2xl p-8 text-center">
                <p className="text-muted-foreground">Aún no hay reseñas públicas para esta tienda.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {reviews.map((review) => {
                  const reviewedProduct = products.find(p => p.id === review.targetId);
                  return (
                    <div key={review.id} className="border rounded-2xl p-6 space-y-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <User className="h-5 w-5 text-primary" />
                          </div>
                          <div>
                            <div className="font-bold">{review.authorName}</div>
                            <div className="flex items-center gap-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star 
                                  key={star} 
                                  className={`h-3 w-3 ${star <= review.rating ? "text-yellow-500 fill-yellow-500" : "text-muted"}`} 
                                />
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground">
                            {new Date(review.createdAt).toLocaleDateString()}
                          </div>
                          {reviewedProduct && (
                            <Link 
                              to={`/product/${reviewedProduct.id}`}
                              className="text-[10px] text-primary hover:underline block mt-1 font-medium"
                            >
                              Ver producto: {reviewedProduct.name}
                            </Link>
                          )}
                        </div>
                      </div>
                      <p className="text-sm leading-relaxed">
                        {review.comment}
                      </p>
                      {review.sellerResponse && (
                        <div className="bg-muted/50 p-4 rounded-xl border-l-4 border-primary space-y-1">
                          <div className="text-xs font-bold text-primary uppercase tracking-wider">Respuesta de {shop.name}</div>
                          <p className="text-sm italic">{review.sellerResponse}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
